import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  User,
  Edit3,
  Loader2,
  AlertCircle,
  Building2,
  Hash,
  Briefcase,
  FileText,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Clock,
  Clock9
} from 'lucide-react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../components/Common/Footer/Footer';
import styles from './ViewLeaveRequest.module.css';
import api from '../../api/axios';
import LeaveRequestDrawer from './LeaveRequestDrawer';
import NotFound from '../Error/NotFound';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import toast from 'react-hot-toast';

const MySwal = withReactContent(Swal);

const ViewLeaveRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = storedUser.role?.toLowerCase() === 'admin';

  const fetchLeaveDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/leave/${id}`);
      if (response.data && response.data.data) {
        setRecord(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching leave details:', error);
      toast.error('Failed to fetch leave details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveDetails();
  }, [id]);

  const handleApprove = async () => {
    MySwal.fire({
      title: 'Approve Leave?',
      text: 'Are you sure you want to approve this leave request?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Approve',
      cancelButtonText: 'No, Cancel',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#64748b'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          await api.post('/leave/approve', {
            leave_id: id,
            status: 'approved',
            approver_id: user.id
          });
          toast.success('Leave approved successfully');
          fetchLeaveDetails();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to approve leave');
        }
      }
    });
  };

  const handleReject = async () => {
    MySwal.fire({
      title: 'Reject Leave?',
      text: 'Are you sure you want to reject this leave request?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Reject',
      cancelButtonText: 'No, Cancel',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const user = JSON.parse(localStorage.getItem('user') || '{}');
          await api.post('/leave/reject', {
            leave_id: id,
            approver_id: user.id
          });
          toast.success('Leave rejected successfully');
          fetchLeaveDetails();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Failed to reject leave');
        }
      }
    });
  };



  const getStatusClass = (status) => {
    if (!status) return '';
    switch (status.toLowerCase()) {
      case 'approved': return styles['status-approved'];
      case 'rejected': return styles['status-rejected'];
      default: return styles['status-pending'];
    }
  };

  const getDurationLabel = (duration) => {
    switch (duration) {
      case 'full_day': return 'Full Day';
      case 'first_half': return 'First Half';
      case 'second_half': return 'Second Half';
      default: return duration;
    }
  };

  if (loading) {
    return (
      <div className={styles['view-leave-layout']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Fetching Leave Request details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!record) {
    return <NotFound />;
  }


  return (
    <div className={styles['view-leave-layout']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'HR', path: '#' },
            { label: 'Leave Requests', path: '/employees/leave-requests' },
            { label: 'View Details', path: `/employees/leave-requests/view/${id}` }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Leave Request Details</h1>
              <p>Detailed view of employee leave application and its current status.</p>
            </div>
            <div className={styles['header-actions']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/employees/leave-requests')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>

              {record.status === 'pending' && isAdmin && (
                <>
                  <button className={styles['btn-primary']} onClick={handleApprove} style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)' }}>
                    <CheckCircle2 size={18} />
                    <span>Approve</span>
                  </button>
                  <button className={styles['btn-danger']} onClick={handleReject}>
                    <XCircle size={18} />
                    <span>Reject</span>
                  </button>
                </>
              )}

              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Edit3 size={18} />
                <span>Edit Request</span>
              </button>
            </div>
          </div>

          <div className={styles['leave-hero-card']}>
            <div className={styles['hero-content']}>
              <div className={styles['hero-left']}>
                <div className={styles['employee-avatar']}>
                  {record.employee?.employee_name?.charAt(0) || '?'}
                </div>
                <div className={styles['employee-info']}>
                  <div className={styles['name-status-row']}>
                    <h2>{record.employee?.employee_name || 'N/A'}</h2>
                    <span className={`${styles['status-pill']} ${getStatusClass(record.status)}`}>
                      {record.status?.toUpperCase() || 'PENDING'}
                    </span>
                  </div>
                  <div className={styles['meta-row']}>
                    <div className={styles['meta-item']}>
                      <Hash size={14} /> <span>{record.employee?.employee_code || 'N/A'}</span>
                    </div>
                    <div className={styles['meta-item']}>
                      <Building2 size={14} /> <span>{record.employee?.department?.departement_name || 'N/A'}</span>
                    </div>
                    <div className={styles['meta-item']}>
                      <Briefcase size={14} /> <span>{record.employee?.designation?.designation_name || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles['hero-right']}>
                <div className={styles['info-box']}>
                  <label>Leave Type</label>
                  <div className={styles['info-value']}>
                    <FileText size={18} style={{ color: '#3b82f6' }} />
                    <span>{record.leave_type?.name || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles['metrics-grid']}>
            <div className={styles['metric-card']}>
              <div className={`${styles['metric-icon-box']} ${styles['blue']}`}><Calendar size={20} /></div>
              <div className={styles['metric-info']}>
                <label>From Date</label>
                <h3>{record.from_date}</h3>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={`${styles['metric-icon-box']} ${styles['blue']}`}><Calendar size={20} /></div>
              <div className={styles['metric-info']}>
                <label>To Date</label>
                <h3>{record.to_date}</h3>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={`${styles['metric-icon-box']} ${styles['green']}`}><Clock9 size={20} /></div>
              <div className={styles['metric-info']}>
                <label>Duration</label>
                <h3>{getDurationLabel(record.leave_duration)}</h3>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={`${styles['metric-icon-box']} ${styles['orange']}`}><Clock size={20} /></div>
              <div className={styles['metric-info']}>
                <label>Created On</label>
                <h3>{new Date(record.created_at).toLocaleDateString()}</h3>
              </div>
            </div>
          </div>

          <div className={styles['details-grid']}>
            <div className={styles['main-column']}>
              <div className={styles['details-card']}>
                <div className={styles['card-header']}>
                  <FileText size={20} />
                  <h3>Reason for Leave</h3>
                </div>
                <div className={styles['reason-box']}>
                  <label>Employee Statement</label>
                  "{record.reason || 'No reason provided.'}"
                </div>
              </div>
            </div>

            <div className={styles['side-column']}>
              <div className={styles['details-card']}>
                <div className={styles['card-header']}>
                  <ShieldCheck size={20} />
                  <h3>Approval Audit</h3>
                </div>
                <div className={styles['compliance-list']}>
                  <div className={styles['info-item']} style={{ marginBottom: '20px' }}>
                    <label>Status</label>
                    <span style={{
                      color: record.status === 'approved' ? '#16a34a' : record.status === 'rejected' ? '#dc2626' : '#d97706',
                      fontWeight: 700
                    }}>
                      {record.status?.toUpperCase() || 'PENDING'}
                    </span>
                  </div>
                  <div className={styles['info-item']} style={{ marginBottom: '20px' }}>
                    <label>Approved By</label>
                    <span>{record.approver?.employee_name || 'Not Yet Processed'}</span>
                  </div>
                  <div className={styles['info-item']}>
                    <label>Processed Date</label>
                    <span>{record.status !== 'pending' ? new Date(record.updated_at).toLocaleString() : '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Footer />
        </main>
      </div>

      <LeaveRequestDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={() => {
          setIsDrawerOpen(false);
          fetchLeaveDetails();
        }}
        request={record}
      />
    </div>
  );
};

export default ViewLeaveRequest;
